import { z } from 'zod';
// ============================================================
// 지표 카탈로그 — 정의 입력·QuerySpec·QueryResult (FIKET-515)
// ============================================================
// ─────────────────────────────────────────────────────────────
// 기본 원시 타입
// ─────────────────────────────────────────────────────────────
/** 지표·소스·차원 식별자 — 소문자 + 숫자 + 밑줄, 2~64자 */
export const MetricKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/);
/** 집계 단위 */
export const MetricGrainSchema = z.enum(['day', 'week', 'month']);
/** 값 표시 형식 */
export const MetricFormatSchema = z.enum(['count', 'krw', 'percent', 'number']);
/** YYYY-MM-DD 날짜 문자열 */
const YMDSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
// ─────────────────────────────────────────────────────────────
// 입력 스키마 (Create/Update 시 클라이언트 → 서버)
// ─────────────────────────────────────────────────────────────
/** 데이터 소스 입력 */
export const MetricSourceInputSchema = z.object({
    key: MetricKeySchema,
    label: z.string().min(1).max(100),
    description: z.string().max(1000).optional(),
    /** FROM 절 SQL (테이블 또는 서브쿼리) */
    fromSql: z.string().min(1).max(4000),
    /** WHERE 조건 추가 SQL (선택) */
    whereSql: z.string().max(4000).optional(),
    /** 시간 필터에 쓸 컬럼명 (소문자·점·밑줄만) */
    timeColumn: z.string().regex(/^[a-z_][a-z0-9_.]*$/),
});
/** 차원 입력 */
export const MetricDimensionInputSchema = z.object({
    key: MetricKeySchema,
    sourceKey: MetricKeySchema,
    label: z.string().min(1).max(100),
    /** GROUP BY / SELECT 에 쓸 표현식 SQL */
    exprSql: z.string().min(1).max(1000),
    /** 값 → 표시 레이블 매핑 (선택) */
    valueLabels: z.record(z.string(), z.string()).optional(),
});
// ─────────── 지표 정의 입력 — discriminatedUnion ───────────
/** 직접 집계 지표 입력 */
const MetricDefinitionMeasureInputSchema = z.object({
    kind: z.literal('measure'),
    key: MetricKeySchema,
    label: z.string().min(1).max(100),
    description: z.string().min(1).max(1000),
    format: MetricFormatSchema,
    sourceKey: MetricKeySchema,
    /** 집계 표현식 SQL (예: SUM(amount)) */
    measureSql: z.string().min(1).max(2000),
});
/** 파생(계산) 지표 입력 */
const MetricDefinitionDerivedInputSchema = z.object({
    kind: z.literal('derived'),
    key: MetricKeySchema,
    label: z.string().min(1).max(100),
    description: z.string().min(1).max(1000),
    format: MetricFormatSchema,
    op: z.enum(['/', '-', '+', '*']),
    leftKey: MetricKeySchema,
    rightKey: MetricKeySchema,
});
/** 지표 정의 입력 — measure | derived */
export const MetricDefinitionInputSchema = z.discriminatedUnion('kind', [
    MetricDefinitionMeasureInputSchema,
    MetricDefinitionDerivedInputSchema,
]);
/** 등록 전 정의 초안. 임시 조회와 정의 관리에서 같은 검증 계약을 사용한다. */
export const DefinitionDraftSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('source'), input: MetricSourceInputSchema }),
    z.object({ kind: z.literal('dimension'), input: MetricDimensionInputSchema }),
    z.object({ kind: z.literal('metric'), input: MetricDefinitionInputSchema }),
]);
// ─────────────────────────────────────────────────────────────
// 쿼리 스펙 / 결과
// ─────────────────────────────────────────────────────────────
/** 분석 질문 스펙 */
export const QuerySpecSchema = z.object({
    /** 조회할 지표 키 목록 (1~4개) */
    metrics: z.array(MetricKeySchema).min(1).max(4),
    /** 그룹화할 차원 키 목록 (최대 2개, 기본 빈 배열) */
    dimensions: z.array(MetricKeySchema).max(2).default([]),
    /** 집계 단위 (선택) */
    grain: MetricGrainSchema.optional(),
    /** 조회 날짜 범위 (YYYY-MM-DD) */
    range: z.object({ from: YMDSchema, to: YMDSchema }),
    /** 차원 필터 (최대 5개, 기본 빈 배열) */
    filters: z
        .array(z.object({
        dimension: MetricKeySchema,
        values: z.array(z.string().max(200)).min(1).max(50),
    }))
        .max(5)
        .default([]),
    /** 비교 기간 (선택) */
    compare: z.enum(['prev_period', 'prev_year']).optional(),
});
// 결과 행 스키마 — compareRows 도 동일 구조
const _QueryResultRowsSchema = z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()])));
/** 쿼리 실행 결과 */
export const QueryResultSchema = z.object({
    fetchedAt: z.string().datetime().optional(),
    cacheHit: z.boolean().optional(),
    sourceErrors: z.array(z.object({
        sourceKey: z.string(),
        code: z.enum(['QUERY_TIMEOUT', 'QUERY_FAILED']),
        period: z.enum(['current', 'compare']),
    })).optional(),
    columns: z.array(z.object({
        key: z.string(),
        label: z.string(),
        kind: z.enum(['bucket', 'dimension', 'metric']),
        format: MetricFormatSchema.optional(),
    })),
    rows: _QueryResultRowsSchema,
    /** 비교 기간 결과 행 (compare 지정 시) */
    compareRows: _QueryResultRowsSchema.optional(),
    truncated: z.boolean(),
    /** 실행된 SQL */
    sql: z.string(),
    /** 쿼리 소요 시간 (ms) */
    elapsedMs: z.number(),
});
// ─────────────────────────────────────────────────────────────
// 저장 스키마 — 입력 + 공통 저장 필드
// ─────────────────────────────────────────────────────────────
/** 저장 공통 필드를 ZodObject에 추가하는 헬퍼 */
function withStoredFields(base) {
    return base.extend({
        id: z.string(),
        version: z.number().int(),
        isActive: z.boolean(),
        createdBy: z.string().nullable(),
        createdAt: z.string(),
    });
}
export const MetricSourceSchema = withStoredFields(MetricSourceInputSchema);
export const MetricDimensionSchema = withStoredFields(MetricDimensionInputSchema);
/** 저장된 지표 정의 — 각 브랜치에 공통 저장 필드 추가 */
export const MetricDefinitionSchema = z.discriminatedUnion('kind', [
    withStoredFields(MetricDefinitionMeasureInputSchema),
    withStoredFields(MetricDefinitionDerivedInputSchema),
]);
// ─────────────────────────────────────────────────────────────
// 카탈로그 전체
// ─────────────────────────────────────────────────────────────
/** 전체 지표 카탈로그 */
export const MetricsCatalogSchema = z.object({
    sources: z.array(MetricSourceSchema),
    dimensions: z.array(MetricDimensionSchema),
    metrics: z.array(MetricDefinitionSchema),
    macros: z.array(z.object({
        name: z.string(),
        signature: z.string(),
        description: z.string(),
    })),
    allowedTables: z.array(z.string()),
});
//# sourceMappingURL=metrics-catalog.js.map